import { ADDRESS_PREFIX_SEPARATOR, TCountryCode } from '@verifime/utils';
import * as yup from 'yup';
import { TRenderType } from '../utils';

export type TMethod = {
  code: string;
  label: string;
  dataCy: string;
  rules: TFormFieldsAndRules;
  options?: any[];
  helpText?: string;
  showRequiredAsterisk?: boolean;
};

export type TGroupedFormFieldsAndRules = {
  basic: TFormFieldsAndRules;
  address: TFormFieldsAndRules;
  methods?: {
    [method: string]: TMethod;
  };
  schema?: {
    [schemaName: string]: TRules;
  };
};

export type TCountryFormValidations = {
  [countryCode in TCountryCode]: TGroupedFormFieldsAndRules;
};

export type TRules = {
  [name: string]: yup.Schema;
};

export type TFromFieldInfo = {
  label: string;
  fieldName: string;
  rules: yup.Schema;
  /**
   * Because the basic form fields are different between countries,
   * Thus add those to render basic fields into the screen.
   */
  renderType?: TRenderType; // Will be rendered into the screen according to the given type
  items?: any[]; // Only be used when the `renderType = RenderType.Select` for now
  /**
   * Used to generate customise items,
   * Only be used when the `renderType = RenderType.Select` for now.
   *
   * `renderItems` has more priority if both `items` and `renderItems` exists
   */
  renderItems?: () => any;
  dataCy?: string;
  maxDate?: Date;
  minDate?: Date;
  showRequiredAsterisk?: boolean; // To control whether show * next to the field in screen
  isNewLine?: boolean; // Rendering to a new line if it is true
  helpText?: string;
  isValueUpperCase?: boolean;
  maxAcceptedItems?: number;
  disabled?: boolean;
};
export type TFormFieldsAndRules = {
  [fieldName: string]: TFromFieldInfo;
};

export function getFieldsRules(
  formFieldsAndRules?: TFormFieldsAndRules | TGroupedFormFieldsAndRules,
  prefix?: keyof typeof formFieldsAndRules,
): TRules {
  let filtered = formFieldsAndRules as TFormFieldsAndRules;
  if (prefix) {
    filtered = formFieldsAndRules[prefix] as TFormFieldsAndRules;
  }

  if (!filtered) {
    return {};
  }
  const fieldsRules = {} as TRules;
  for (const { fieldName, rules } of Object.values(filtered)) {
    fieldsRules[fieldName] = rules;
  }

  return fieldsRules;
}

export function getFieldsNamesFromFieldsAndRules(fieldsAndRules: TFormFieldsAndRules): string[] {
  const fieldsNameArray: string[] = [];
  if (!fieldsAndRules) {
    return fieldsNameArray;
  }
  for (const [_, fieldInfo] of Object.entries(fieldsAndRules)) {
    fieldsNameArray.push(fieldInfo.fieldName);
  }
  return fieldsNameArray;
}

export function getFieldsPrefixFromFieldsAndRules(fieldsAndRules: TFormFieldsAndRules): string {
  const fieldsNames = getFieldsNamesFromFieldsAndRules(fieldsAndRules);
  const prefix = fieldsNames[0].split(ADDRESS_PREFIX_SEPARATOR)[0];
  return prefix ?? '';
}
